import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Typography,
  Alert,
  Stack
} from '@mui/material';
import { SelectChangeEvent } from '@mui/material/Select';
import { useAuth } from '../../contexts/AuthContext';
import { createPost } from '../../services/postService';
import { CAUSES } from '../layout/causes';

interface PostSubmitFormProps {
  onSubmitSuccess?: () => void;
}

export default function PostSubmitForm({ onSubmitSuccess }: PostSubmitFormProps) {
  const { currentUser } = useAuth();
  const [content, setContent] = useState('');
  const [selectedCauses, setSelectedCauses] = useState<string[]>([]);
  const [primaryCause, setPrimaryCause] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleContentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setContent(e.target.value);
    setError(null);
  };

  const handleCauseChange = (e: SelectChangeEvent<string[]>) => {
    const causes = e.target.value as string[];
    if (causes.length > 3) {
      return;
    }
    setSelectedCauses(causes);
    if (!primaryCause && causes.length > 0) {
      setPrimaryCause(causes[0]);
    }
  };

  const handlePrimaryCauseChange = (e: SelectChangeEvent<string>) => {
    const selectedCause = e.target.value;
    setPrimaryCause(selectedCause);
    
    // Automatically add the primary cause to the selected causes if it's not already there
    if (selectedCause && !selectedCauses.includes(selectedCause)) {
      setSelectedCauses(prev => [...prev, selectedCause]);
    }
  };

  const handleRemoveCause = (causeToRemove: string) => {
    setSelectedCauses(prev => prev.filter(cause => cause !== causeToRemove));
    if (primaryCause === causeToRemove) {
      setPrimaryCause(selectedCauses.find(cause => cause !== causeToRemove) || '');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser) {
      setError('You must be logged in to create a post.');
      return;
    }

    if (!content.trim()) {
      setError('Please enter some content for your post.');
      return;
    }

    if (selectedCauses.length === 0) {
      setError('Please select at least one cause.');
      return;
    }

    if (selectedCauses.length > 3) {
      setError('You can only select up to 3 causes.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await createPost(content, selectedCauses, primaryCause, currentUser);
      setContent('');
      setSelectedCauses([]);
      setPrimaryCause('');
      if (onSubmitSuccess) {
        onSubmitSuccess();
      }
    } catch (error) {
      console.error('Error creating post:', error);
      setError('Failed to create post. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ width: '100%' }}>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Share your thoughts, ideas, or updates with the community.
      </Typography>

      <TextField
        fullWidth
        multiline
        rows={4}
        label="What's on your mind?"
        value={content}
        onChange={handleContentChange}
        placeholder="Share your thoughts, ideas, or updates..."
        variant="outlined"
        sx={{ mb: 3 }}
        error={!!error && !content.trim()}
        helperText={error && !content.trim() ? error : ''}
      />

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Primary Cause</InputLabel>
        <Select
          value={primaryCause}
          label="Primary Cause"
          onChange={handlePrimaryCauseChange}
          required
        >
          {CAUSES.map((cause) => (
            <MenuItem key={cause.id} value={cause.id}>
              {cause.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl fullWidth sx={{ mb: 2 }}>
        <InputLabel>Add Causes (up to 3)</InputLabel>
        <Select
          multiple
          value={selectedCauses}
          label="Add Causes (up to 3)"
          onChange={handleCauseChange}
          disabled={selectedCauses.length >= 3}
          renderValue={(selected) => (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {selected.map((value) => (
                <Chip 
                  key={value} 
                  label={CAUSES.find(c => c.id === value)?.name || value}
                  onDelete={() => handleRemoveCause(value)}
                  size="small"
                />
              ))}
            </Box>
          )}
        >
          {CAUSES.map((cause) => (
            <MenuItem key={cause.id} value={cause.id}>
              {cause.name}
            </MenuItem>
          ))}
        </Select>
        {selectedCauses.length >= 3 && (
          <Typography variant="caption" color="error" sx={{ mt: 1 }}>
            Maximum of 3 causes reached
          </Typography>
        )}
      </FormControl>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Stack direction="row" spacing={2} justifyContent="flex-end">
        <Button
          type="submit"
          variant="contained"
          disabled={isSubmitting || !content.trim() || selectedCauses.length === 0}
          sx={{
            textTransform: 'uppercase',
            fontWeight: 'bold',
            letterSpacing: '0.5px'
          }}
        >
          {isSubmitting ? 'Creating...' : 'Create Post'}
        </Button>
      </Stack>
    </Box>
  );
} 